/**
 * Pattern Library Service for Cross-Report Pattern Learning
 *
 * Analyzes precursorPatterns across multiple case studies to:
 * 1. Track pattern frequency and confidence scores
 * 2. Identify common patterns across incidents
 * 3. Update risk alert matching thresholds based on pattern prevalence
 *
 * Uses Hugo_PatternLibrary Firestore collection for persistence.
 */
import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { collections } from '../utils/firestore';

// Register the new collection
const PATTERN_LIBRARY_COLLECTION = 'Hugo_PatternLibrary';

// ---------------------------------------------------------------------------
// Pattern Analysis
// ---------------------------------------------------------------------------

/**
 * Known pattern types and their base descriptions.
 * These serve as canonical pattern definitions that CSB reports can match against.
 */
export const KNOWN_PATTERN_TYPES = {
  safety_recommendation_ignored: {
    label: 'Safety Recommendation Ignored',
    description: 'Safety-critical recommendations from technical experts are repeatedly deferred or not implemented.',
    baseElement: 'driftWork',
    baseThreshold: 7,
  },
  normalization_of_deviance: {
    label: 'Normalization of Deviance',
    description: 'Gradual acceptance of degraded conditions as normal, with risk perception declining over time.',
    baseElement: 'driftPerception',
    baseThreshold: 7,
  },
  inspection_gap: {
    label: 'Inspection Gap',
    description: 'Inspection programs do not adequately cover known damage mechanisms or intervals are extended without justification.',
    baseElement: 'driftWork',
    baseThreshold: 6,
  },
  swa_reluctance: {
    label: 'Stop Work Authority Reluctance',
    description: 'Workers reluctant to exercise Stop Work Authority due to cultural pressure or fear.',
    baseElement: 'traps',
    baseThreshold: 6,
  },
  budget_over_safety: {
    label: 'Budget Over Safety',
    description: 'Budget constraints or cost avoidance override safety-critical maintenance or investment decisions.',
    baseElement: 'roadblocks',
    baseThreshold: 6,
  },
  organizational_siloing: {
    label: 'Organizational Siloing',
    description: 'Departments or functions operate in isolation, preventing integrated risk assessment.',
    baseElement: 'roadblocks',
    baseThreshold: 5,
  },
  inadequate_training: {
    label: 'Inadequate Training',
    description: 'Workers lack sufficient training for hazard recognition, emergency procedures, or equipment operation.',
    baseElement: 'driftWork',
    baseThreshold: 5,
  },
  management_of_change_failure: {
    label: 'Management of Change Failure',
    description: 'Changes to processes, equipment, or personnel are not properly reviewed for safety impact.',
    baseElement: 'landmines',
    baseThreshold: 6,
  },
  process_safety_culture: {
    label: 'Process Safety Culture Deficit',
    description: 'Organization lacks a strong process safety culture, treating safety as secondary to production.',
    baseElement: 'driftPerception',
    baseThreshold: 6,
  },
  hidden_degradation: {
    label: 'Hidden Degradation',
    description: 'Equipment or system degradation not visible during normal inspection, requiring specialized monitoring.',
    baseElement: 'landmines',
    baseThreshold: 6,
  },
};

/**
 * Calculate adjusted threshold based on pattern frequency.
 *
 * As a pattern appears across more case studies, its detection threshold
 * decreases (becomes more sensitive), making early warnings more likely.
 *
 * Formula: adjustedThreshold = baseThreshold - log2(frequency) * 0.5
 * Minimum threshold: 3 (to avoid false positives)
 *
 * @param {number} baseThreshold - Original threshold value
 * @param {number} frequency - Number of case studies containing this pattern
 * @returns {number} Adjusted threshold (lower = more sensitive)
 */
export function calculateAdjustedThreshold(baseThreshold, frequency) {
  if (frequency <= 1) return baseThreshold;
  const adjustment = Math.log2(frequency) * 0.5;
  return Math.max(3, Math.round((baseThreshold - adjustment) * 10) / 10);
}

/**
 * Calculate pattern confidence score based on frequency and consistency.
 *
 * @param {number} frequency - Number of case studies with this pattern
 * @param {number} totalCaseStudies - Total number of case studies analyzed
 * @returns {{ confidence: number, level: string }}
 */
export function calculateConfidence(frequency, totalCaseStudies) {
  if (totalCaseStudies === 0) return { confidence: 0, level: 'unknown' };

  const prevalence = frequency / totalCaseStudies;
  let confidence;

  if (frequency >= 5) {
    confidence = Math.min(0.95, 0.7 + prevalence * 0.25);
  } else if (frequency >= 3) {
    confidence = 0.5 + prevalence * 0.2;
  } else if (frequency >= 2) {
    confidence = 0.3 + prevalence * 0.15;
  } else {
    confidence = 0.15 + prevalence * 0.1;
  }

  confidence = Math.round(confidence * 100) / 100;

  let level;
  if (confidence >= 0.8) level = 'high';
  else if (confidence >= 0.5) level = 'medium';
  else if (confidence >= 0.3) level = 'low';
  else level = 'emerging';

  return { confidence, level };
}

/**
 * Extract precursor patterns from a case study document.
 *
 * @param {Object} caseStudy - Case study document from Firestore
 * @returns {Array<{patternType: string, description: string, element: string, threshold: number}>}
 */
export function extractPatterns(caseStudy) {
  const patterns = [];

  // Extract from precursorPatterns array
  if (Array.isArray(caseStudy.precursorPatterns)) {
    for (const p of caseStudy.precursorPatterns) {
      patterns.push({
        patternType: p.patternType,
        description: p.description || '',
        element: p.matchCriteria?.element || '',
        threshold: p.matchCriteria?.value || 5,
        caseStudyId: caseStudy.id,
        caseStudyTitle: caseStudy.title || '',
        industry: caseStudy.industry || '',
      });
    }
  }

  return patterns;
}

/**
 * Run cross-reference analysis across all case studies.
 * Aggregates patterns, calculates frequencies, confidence scores,
 * and adjusted thresholds.
 *
 * @param {Array<Object>} caseStudies - All case study documents
 * @returns {Array<Object>} Pattern library entries sorted by frequency desc
 */
export function crossReferenceAnalysis(caseStudies) {
  const patternMap = new Map();
  const totalCaseStudies = caseStudies.length;

  for (const cs of caseStudies) {
    const patterns = extractPatterns(cs);

    for (const p of patterns) {
      if (!patternMap.has(p.patternType)) {
        const known = KNOWN_PATTERN_TYPES[p.patternType];
        patternMap.set(p.patternType, {
          patternType: p.patternType,
          label: known?.label || p.patternType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          description: known?.description || p.description,
          element: known?.baseElement || p.element,
          baseThreshold: known?.baseThreshold || p.threshold,
          frequency: 0,
          caseStudyIds: [],
          caseStudyTitles: [],
          industries: new Set(),
          thresholdValues: [],
        });
      }

      const entry = patternMap.get(p.patternType);
      if (!entry.caseStudyIds.includes(p.caseStudyId)) {
        entry.frequency++;
        entry.caseStudyIds.push(p.caseStudyId);
        entry.caseStudyTitles.push(p.caseStudyTitle);
      }
      if (p.industry) entry.industries.add(p.industry);
      entry.thresholdValues.push(p.threshold);
    }
  }

  // Calculate confidence and adjusted thresholds
  const results = [];
  for (const entry of patternMap.values()) {
    const { confidence, level } = calculateConfidence(entry.frequency, totalCaseStudies);
    const adjustedThreshold = calculateAdjustedThreshold(entry.baseThreshold, entry.frequency);

    results.push({
      patternType: entry.patternType,
      label: entry.label,
      description: entry.description,
      element: entry.element,
      baseThreshold: entry.baseThreshold,
      adjustedThreshold,
      frequency: entry.frequency,
      confidence,
      confidenceLevel: level,
      totalCaseStudies,
      caseStudyIds: entry.caseStudyIds,
      caseStudyTitles: entry.caseStudyTitles,
      industries: Array.from(entry.industries),
      avgThreshold: entry.thresholdValues.length > 0
        ? Math.round((entry.thresholdValues.reduce((a, b) => a + b, 0) / entry.thresholdValues.length) * 10) / 10
        : entry.baseThreshold,
    });
  }

  // Sort by frequency descending, then confidence
  results.sort((a, b) => b.frequency - a.frequency || b.confidence - a.confidence);

  return results;
}

// ---------------------------------------------------------------------------
// Firestore Operations — Hugo_PatternLibrary
// ---------------------------------------------------------------------------

/**
 * Save the pattern library analysis results to Firestore.
 *
 * Uses a single document per pattern type (upsert via setDoc merge).
 *
 * @param {Array<Object>} patterns - Results from crossReferenceAnalysis
 * @returns {Promise<number>} Number of patterns saved
 */
export async function savePatternLibrary(patterns) {
  let count = 0;

  for (const pattern of patterns) {
    const docRef = doc(db, PATTERN_LIBRARY_COLLECTION, pattern.patternType);
    await setDoc(docRef, {
      ...pattern,
      updatedAt: Timestamp.now(),
    }, { merge: true });
    count++;
  }

  return count;
}

/**
 * Get the full pattern library from Firestore.
 *
 * @returns {Promise<Array<Object>>} All patterns sorted by frequency desc
 */
export async function getPatternLibrary() {
  const colRef = collection(db, PATTERN_LIBRARY_COLLECTION);
  const q = query(colRef, orderBy('frequency', 'desc'));

  try {
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    // Fallback if index not available
    console.warn('Pattern library query failed, using unordered fetch:', error.message);
    const snapshot = await getDocs(colRef);
    const patterns = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    patterns.sort((a, b) => (b.frequency || 0) - (a.frequency || 0));
    return patterns;
  }
}

/**
 * Subscribe to real-time pattern library updates.
 *
 * @param {Function} callback - Called with array of pattern objects
 * @returns {Function} Unsubscribe function
 */
export function subscribeToPatternLibrary(callback) {
  const colRef = collection(db, PATTERN_LIBRARY_COLLECTION);

  return onSnapshot(colRef, (snapshot) => {
    const patterns = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    patterns.sort((a, b) => (b.frequency || 0) - (a.frequency || 0));
    callback(patterns);
  }, (error) => {
    console.error('Pattern library subscription error:', error);
    callback([]);
  });
}

/**
 * Get pattern data for a specific case study (which patterns appear in it).
 *
 * @param {string} caseStudyId - The case study ID
 * @param {Array<Object>} patternLibrary - Full pattern library
 * @returns {Array<Object>} Patterns that include this case study
 */
export function getPatternsForCaseStudy(caseStudyId, patternLibrary) {
  return patternLibrary.filter(
    (p) => Array.isArray(p.caseStudyIds) && p.caseStudyIds.includes(caseStudyId)
  );
}

/**
 * Run a full cross-reference analysis and save results.
 *
 * This is the main entry point: fetches all case studies, analyzes patterns,
 * and persists the results to Hugo_PatternLibrary.
 *
 * @returns {Promise<{patternsFound: number, caseStudiesAnalyzed: number}>}
 */
export async function runFullAnalysis() {
  // Fetch all published case studies
  const colRef = collection(db, collections.caseStudies);
  const q = query(colRef, where('status', '==', 'published'));
  const snapshot = await getDocs(q);
  const caseStudies = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Run cross-reference analysis
  const patterns = crossReferenceAnalysis(caseStudies);

  // Save to Firestore
  await savePatternLibrary(patterns);

  return {
    patternsFound: patterns.length,
    caseStudiesAnalyzed: caseStudies.length,
  };
}
